<template>
    <NDatePicker
        ref="datePickerDOM"
        class="date-range-picker"
        type="daterange"
        v-bind="$attrs"
        format="yyyy-MM-dd"
        value-format="yyyy-MM-dd"
        bind-calendar-months
        :size="size"
        :clearable="false"
        :updateValueOnClose="isUpdateValueOnClose"
        :separator="separator"
        :inputReadonly="inputReadonly"
        :shortcuts="shortcutsRef"
        :is-date-disabled="disabledDate || calcRangeDateDisabled"
        :defaultValue="defaultValue"
        :value="dateRangeValue"
        @update:value="updateDateRangeValue"
    >
        <template #date-icon>
            <CalendarSvg class="icon-calendar" />
        </template>
    </NDatePicker>
</template>

<script setup lang="ts">
import CalendarSvg from '@/assets/svg/icn_calendar.svg?component';
import {DateTime} from 'luxon';
import {NDatePicker} from 'naive-ui';
import {Ref, computed, ref, toRefs} from 'vue';

// props, emits, libs
const props = withDefaults(
    defineProps<{
        // modelValue: [string | number | undefined, string | number | undefined];
        fromDate: string;
        toDate: string;
        /** 미래 날짜 선택 lock 여부 */
        disableFutureDate?: boolean;
        /** 오늘 날짜 선택 lock 여부 */
        disableToday?: boolean;
        /** DateRangePicker 하단 Footer 내의 custom 액션 버튼 */
        shortcuts?: Record<string, [number, number]>;
        /** fromDate, endDate 사이의 구분자 표기 */
        separator?: string;
        /** DateRangePicker Input 키보드 제어 가능여부 */
        inputReadonly?: boolean;
        /** DateRangePicker Input 사이즈 */
        size?: 'small' | 'medium' | 'large';
        /** 최대 선택 가능한 날짜 범위 */
        maxRange?: {type: 'days' | 'weeks' | 'months' | 'years'; value: number};
        /** 날짜 선택후 확인 버튼을 눌러서 캘린더를 닫지 않고, 캘린더 바깥 영역을 클릭하여 캘린더를 닫았을때에도 값 업데이트할지 여부 */
        isUpdateValueOnClose?: boolean;
        resetDateRange?: Function;
        disabledDate?: (timestamp: number) => boolean;
    }>(),
    {
        disableFutureDate: true,
        disableToday: false,
        separator: '〜',
        inputReadonly: true,
        size: 'large',
        maxRange: () => {
            return {type: 'months', value: 1};
        },
        isUpdateValueOnClose: true,
    },
);

const emits = defineEmits<{
    (e: 'update:fromDate', value: string): void;
    (e: 'update:toDate', value: string): void;
}>();

// const
const defaultValue = [
    DateTime.fromISO(props.fromDate).toMillis(),
    DateTime.fromISO(props.toDate).toMillis(),
] as [number, number]; // 기본값은 한달전 ~ 오늘

// ref, reactive
const datePickerDOM = ref<HTMLElement>();

const {shortcuts} = toRefs(props);

const shortcutsRef: Ref<Record<string, [number, number]>> = ref(
    shortcuts?.value ?? {
        오늘: [Date.now(), Date.now()],
        일주일: [new Date().setDate(new Date().getDate() - 7), Date.now()],
        '1개월': [new Date().setMonth(new Date().getMonth() - 1), Date.now()],
    },
);

// computed
const fromDate = computed(() => DateTime.fromISO(props.fromDate).toMillis());
const toDate = computed(() => DateTime.fromISO(props.toDate).toMillis());

const dateRangeValue = computed((): [number, number] => [
    fromDate.value,
    toDate.value,
]);

// methods
const updateDateRangeValue = (
    updatedValue: [number, number],
    formattedValue: [string, string],
) => {
    if (!updatedValue) return;

    emits('update:fromDate', formattedValue[0]);
    emits('update:toDate', formattedValue[1]);
};

/**
 * 캘린더의 선택 불가능 날짜 계산 함수
 * @param current
 * @param inputPhase
 * @param inputValue
 */
const calcRangeDateDisabled = (
    current: number,
    inputPhase: 'start' | 'end',
    inputValue: [number, number] | null,
) => {
    // days
    if (
        props.maxRange &&
        props.maxRange.type === 'days' &&
        props.maxRange.value &&
        inputValue !== null
    ) {
        if (
            inputPhase === 'start' &&
            new Date(inputValue[1]).setDate(
                new Date(inputValue[1]).getDate() - props.maxRange.value,
            ) > current
        ) {
            // 다음꺼 누를 차례가 start 일때, start를 end의 X일전 날짜까지만 선택가능 하게함
            return true;
        }

        if (
            inputPhase === 'end' &&
            new Date(inputValue[0]).setDate(
                new Date(inputValue[0]).getDate() + props.maxRange.value,
            ) < current
        ) {
            // 다음꺼 누를 차례가 end 일때, end를 start의 X일전 날짜까지만 선택가능 하게함
            return true;
        }
    }

    // weeks
    if (
        props.maxRange &&
        props.maxRange.type === 'weeks' &&
        props.maxRange.value &&
        inputValue !== null
    ) {
        if (
            inputPhase === 'start' &&
            new Date(inputValue[1]).setDate(
                new Date(inputValue[1]).getDate() - 7 * props.maxRange.value,
            ) > current
        ) {
            // 다음꺼 누를 차례가 start 일때, start를 end의 X주전 날짜까지만 선택가능 하게함
            return true;
        }

        if (
            inputPhase === 'end' &&
            new Date(inputValue[0]).setDate(
                new Date(inputValue[0]).getDate() + 7 * props.maxRange.value,
            ) < current
        ) {
            // 다음꺼 누를 차례가 end 일때, end를 start의 X주뒤 날짜까지만 선택가능 하게함
            return true;
        }
    }

    // months
    if (
        props.maxRange &&
        props.maxRange.type === 'months' &&
        props.maxRange.value &&
        inputValue !== null
    ) {
        if (inputPhase === 'start') {
            if (
                new Date(inputValue[1]).setMonth(
                    new Date(inputValue[1]).getMonth() - props.maxRange.value,
                ) > current
            ) {
                // 다음꺼 누를 차례가 start 일때, start를 end의 X달전 날짜까지만 선택가능 하게함
                return true;
            }
        }

        if (inputPhase === 'end') {
            if (
                new Date(inputValue[0]).setMonth(
                    new Date(inputValue[0]).getMonth() + props.maxRange.value,
                ) < current
            ) {
                // 다음꺼 누를 차례가 start 일때, start를 end의 X달후 날짜까지만 선택가능 하게함
                return true;
            }
        }
    }

    // year
    if (
        props.maxRange &&
        props.maxRange.type === 'years' &&
        props.maxRange.value &&
        inputValue !== null
    ) {
        if (
            inputPhase === 'start' &&
            new Date(inputValue[1]).setDate(
                new Date(inputValue[1]).getDate() - 365 * props.maxRange.value,
            ) > current
        ) {
            // 다음꺼 누를 차례가 start 일때, start를 end의 X년전 날짜까지만 선택가능 하게함
            return true;
        }

        if (
            inputPhase === 'end' &&
            new Date(inputValue[0]).setDate(
                new Date(inputValue[0]).getDate() + 365 * props.maxRange.value,
            ) < current
        ) {
            // 다음꺼 누를 차례가 end 일때, end를 start의 X년뒤 날짜까지만 선택가능 하게함
            return true;
        }
    }

    if (props.disableToday) {
        // 과거 날짜만 선택 가능하게 할지 말지 여부 (오늘 날짜도 선택 안됨)
        return current > Date.now() - 86400000;
    }

    if (props.disableFutureDate) {
        // 미래날짜 선택 가능하게 할지 말지 여부

        if (current) {
            // NOTE: startDate, endDate 모두 오늘날짜 이후 선택 안됨

            return (
                new Date().getTime() < current &&
                new Date(Date.now()).getTime() < current
            );
        }
    }

    // 위의 조건들 외의 날짜는 선택 가능
    return false;
};
</script>

<style lang="scss" scoped>
.date-range-picker {
    :deep() {
        .n-input {
            .n-input-wrapper,
            &__input-el,
            &__separator {
                cursor: pointer;
            }

            &__suffix {
                margin-right: 18px;

                .icon-calendar {
                    > path {
                        fill: $black_40 !important;
                    }
                }
            }

            &:hover,
            &--focus {
                .icon-calendar {
                    > path {
                        fill: $primary !important;
                    }
                }
            }
        }
    }
}
</style>
